import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, RefreshCw, Loader2, Search, MessageSquare, Quote, Newspaper, Users, Mail, Calendar, ShoppingBag, HandHeart, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'testimonials', label: 'Testimonials', icon: Quote, endpoint: '/api/admin/testimonials', nameField: 'name', detailField: 'quote', deleteEndpoint: '/api/admin/testimonials' },
  { id: 'updates', label: 'Community Updates', icon: Newspaper, endpoint: '/api/admin/updates', nameField: 'title', detailField: 'content', deleteEndpoint: '/api/admin/updates' },
  { id: 'contacts', label: 'Contact Messages', icon: MessageSquare, endpoint: '/api/admin/contacts', nameField: 'name', detailField: 'message', deleteEndpoint: '/api/admin/contacts' },
  { id: 'subscribers', label: 'Newsletter Subscribers', icon: Mail, endpoint: '/api/admin/subscribers', nameField: 'email', detailField: '', deleteEndpoint: '/api/admin/subscribers' },
  { id: 'volunteers', label: 'Volunteers', icon: HandHeart, endpoint: '/api/admin/volunteers', nameField: 'name', detailField: 'email', deleteEndpoint: '/api/admin/volunteers' },
  { id: 'attendees', label: 'Event Attendees', icon: Users, endpoint: '/api/admin/events/attendees/all', nameField: 'name', detailField: 'email', extraField: 'event_title', deleteEndpoint: '/api/admin/events/attendees' },
  { id: 'events', label: 'Events', icon: Calendar, endpoint: '/api/admin/events', nameField: 'title', detailField: 'date', deleteEndpoint: '/api/admin/events' },
  { id: 'donations', label: 'Donations', icon: ShoppingBag, endpoint: '/api/admin/donations', nameField: 'donor_name', detailField: 'amount', deleteEndpoint: null },
];

const ContentSection = ({ section, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}${section.endpoint}`, { withCredentials: true });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch { setItems([]); }
    setLoading(false);
  }, [section.endpoint]);

  useEffect(() => {
    if (expanded) fetchItems();
  }, [expanded, fetchItems]);

  const handleDelete = async (item) => {
    const itemId = item.id || item.email;
    const displayName = item[section.nameField] || item.email || itemId;
    if (!window.confirm(`Delete "${displayName}"? This cannot be undone.`)) return;
    setDeleting(itemId);
    try {
      await axios.delete(`${BACKEND_URL}${section.deleteEndpoint}/${itemId}`, { withCredentials: true });
      setItems(prev => prev.filter(i => (i.id || i.email) !== itemId));
      if (onRefresh) onRefresh();
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.detail || 'Unknown error'));
    }
    setDeleting(null);
  };

  const filtered = items.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (item[section.nameField] || '').toLowerCase().includes(s) ||
      (item[section.detailField] || '').toString().toLowerCase().includes(s) ||
      (item.email || '').toLowerCase().includes(s) ||
      (item.entry_id || '').toLowerCase().includes(s);
  });

  const Icon = section.icon;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`content-section-${section.id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#F8F9FA] transition-colors"
        data-testid={`expand-${section.id}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-4 h-4 text-[#BA1826]" />
          <span className="font-heading font-bold text-sm text-[#0A0A0A]">{section.label}</span>
          {expanded && !loading && (
            <span className="text-[10px] bg-[#F1F5F9] text-[#4B5563] px-2 py-0.5 rounded-full">{items.length} items</span>
          )}
        </div>
        <span className="text-xs text-[#4B5563]">{expanded ? 'Collapse' : 'Expand'}</span>
      </button>

      {expanded && (
        <div className="border-t border-[#E5E7EB] px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-4">No items found.</p>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
                    data-testid={`search-${section.id}`} />
                </div>
                <button onClick={fetchItems} className="text-xs text-[#4B5563] hover:text-[#BA1826] p-2" title="Refresh">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {filtered.map((item, idx) => {
                  const itemId = item.id || item.email || idx;
                  const name = item[section.nameField] || item.email || 'Unknown';
                  const detail = section.detailField ? (
                    section.detailField === 'amount' ? `$${item.amount || 0}` :
                    String(item[section.detailField] || '').substring(0, 80)
                  ) : '';
                  const extra = section.extraField ? item[section.extraField] || '' : '';
                  const isTest = (name + ' ' + detail + ' ' + extra).toLowerCase().includes('test');

                  return (
                    <div key={itemId}
                      className={`flex items-center gap-3 px-3 py-2 rounded text-xs ${isTest ? 'bg-red-50 border border-red-200' : 'bg-[#F8F9FA]'}`}
                      data-testid={`content-item-${itemId}`}>
                      {isTest && <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-[#0A0A0A] block truncate">{name}</span>
                        {detail && <span className="text-[#4B5563] block truncate">{detail}</span>}
                        {extra && <span className="text-[#9CA3AF] block truncate">{extra}</span>}
                      </div>
                      <span className="text-[9px] text-[#9CA3AF] flex-shrink-0">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
                      {section.deleteEndpoint && (
                        <button onClick={() => handleDelete(item)}
                          disabled={deleting === itemId}
                          className="text-red-400 hover:text-red-600 flex-shrink-0 disabled:opacity-50"
                          title="Delete"
                          data-testid={`delete-${section.id}-${itemId}`}>
                          {deleting === itemId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {filtered.length !== items.length && (
                <p className="text-[10px] text-[#9CA3AF] mt-2">Showing {filtered.length} of {items.length}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AdminContentManager = () => {
  return (
    <div className="space-y-4" data-testid="content-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Content Manager</h2>
          <p className="text-xs text-[#4B5563]">View and remove items from any section. Items containing "test" are highlighted in red.</p>
        </div>
      </div>
      {SECTIONS.map(section => (
        <ContentSection key={section.id} section={section} />
      ))}
    </div>
  );
};

export default AdminContentManager;
